import React from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import PrepareTrackingDataHelper from '@Services/PrepareTrackingDataHelper';
import ToggleButton from '@Components/Results/reactions/reaction-details/ToggleButton';
import externalUrl from '@Utils/ExternalUrl';
import UtilsHelper from '@Components/Results/services/utils-helper.service';
import ReactReactionDetailsHelper from '@Components/Results/services/react-reaction-details-helper.service';
import FullText from '@Components/Results/reactions/reaction-details/FullText';
import CitationsCustomLinkComponent from '@Components/Results/reactions/reaction-details/citations-custom-link.component';
import Catalyst from '@Services/Catalyst';
import { getValueByNs } from '@Hooks/LibraryInitialization/MoreUnderscore';

export default class CitationsButtonsComponent extends React.PureComponent {
  abstractClick = debounce(
    () => {
      const { citation, metadata } = this.props;
      ReactReactionDetailsHelper.openTitlesAbstractsDialog(citation, metadata);
    },
    500,
    { leading: true }
  );

  // eslint-disable-next-line no-useless-constructor
  constructor(props) {
    super(props);
    this.state = {
      updatedCitedByUrl: '',
    };
  }

  // eslint-disable-next-line react/no-unused-class-component-methods, react/sort-comp
  updateUrls = async () => {
    const { citation } = this.props;
    if (citation.issueCitedByCounter && citation.issueCitedByCounter > 0) {
      const updatedCitedByUrl = await Catalyst.appendVisitorIDToUrl(externalUrl(citation.citedBy));
      this.setState((prevState) => ({
        ...prevState,
        updatedCitedByUrl,
      }));
    }
  };

  componentDidMount() {
    this.updateUrls();
  }

  getTrackingProps() {
    const { citation, metadata } = this.props;
    return {
      'data-click-delegation': '',
      'data-tracking': 'true',
      'data-tracking-context': metadata.context,
      'data-tracking-preview': getValueByNs(this.props, 'metadata.isViewPreview'),
      'data-tracking-track-link-location-in-results': true,
      'data-tracking-from-rt': metadata.fromRT,
      ...PrepareTrackingDataHelper.prepare(citation),
    };
  }

  goToContext = (context, item, requestParams) => {
    if (this.detailsButton.getAttribute('disabled')) return;
    this.detailsButton.setAttribute('disabled', 'true');
    ReactReactionDetailsHelper.goToContext(context, item, requestParams);
  };

  render() {
    const {
      metadata,
      citation,
      index,
      isAbstractExpanded,
      toggleAbstractExpanded,
      onIssueCitedCounterClick,
      onFullTextLinkClick,
      onAbstractOpen,
    } = this.props;
    const { updatedCitedByUrl } = this.state;
    const { oldTrackingDisabled } = metadata;
    const preparedTrackingData = this.getTrackingProps();
    const fullTextTrackingData = oldTrackingDisabled
      ? {}
      : {
          ...preparedTrackingData,
          'data-tracking-type': 'linkout',
          'data-tracking-action': 'full text',
          'data-doi': UtilsHelper.stripTags(citation.doi),
        };
    const citedTrackingData = oldTrackingDisabled
      ? {}
      : {
          ...preparedTrackingData,
          'data-tracking-type': 'linkout',
          'data-tracking-action': 'cited',
        };
    const abstractLinkTrackingData = oldTrackingDisabled
      ? {}
      : {
          ...preparedTrackingData,
          'data-tracking-type': 'contentview',
          'data-tracking-action': 'abstract',
        };
    return (
      <div className="result-detail-row-btns els-btn-group-left">
        {citation.abstractText && (
          <ToggleButton
            ariaControls={`reaction-citations-abstract-${index}`}
            expanded={!!isAbstractExpanded}
            toggleExpand={toggleAbstractExpanded}
            expandedLabel="Abstract"
            collapsedLabel="Abstract"
            prepareTrackingData={citation}
            dataTrackingAction="abstract"
            isViewPreview={metadata.isViewPreview}
            className="els-btn-xs blue-compact e2e-abstract-text"
          />
        )}
        {citation.fullTextCustom && (
          <CitationsCustomLinkComponent citation={citation} fullTextTrackingData={fullTextTrackingData} onClick={onFullTextLinkClick} />
        )}
        {!citation.fullTextCustom && (
          <FullText citation={citation} trackingAttributes={{ fullTextTrackingData }} metadata={metadata} />
        )}
        {citation.issueCitedByCounter && citation.issueCitedByCounter > 0 && (
          // eslint-disable-next-line react/jsx-no-target-blank
          <a
            className="els-btn els-btn-link blue-compact els-btn-xs e2e-cited"
            href={updatedCitedByUrl}
            tabIndex="0"
            target="_blank"
            {...citedTrackingData}
            onClick={() => onIssueCitedCounterClick(citation)}>
            <span>{`Cited ${citation.issueCitedByCounter} times`}</span>
            <svg className="els-btn-icon">
              <use xlinkHref="#icon-up-right" />
            </svg>
          </a>
        )}
        {citation && (
          // eslint-disable-next-line react/button-has-type
          <button
            className="els-btn els-btn-link blue-compact els-btn-xs e2e-details"
            ref={(detailsButton) => {
              this.detailsButton = detailsButton;
            }}
            onClick={() => this.goToContext('citations', citation, metadata)}>
            <span>Details</span>
            <svg className="els-btn-icon">
              <use xlinkHref="#icon-chevron-right" />
            </svg>
          </button>
        )}
        {citation.showAbstractLink && (
          // eslint-disable-next-line react/button-has-type
          <button
            className="els-btn els-btn-link blue-compact els-btn-xs e2e-abstract"
            onClick={() => {
              onAbstractOpen(citation);
              this.abstractClick();
            }}
            {...abstractLinkTrackingData}>
            <span>Abstract</span>
            <svg className="els-btn-icon">
              <use xlinkHref="#icon-chevron-right" />
            </svg>
          </button>
        )}
      </div>
    );
  }
}

CitationsButtonsComponent.defaultProps = {
  onIssueCitedCounterClick: () => null,
  onFullTextLinkClick: () => null,
  onAbstractOpen: () => null,
};

CitationsButtonsComponent.propTypes = {
  metadata: MetadataPropTypesShape,
  citation: PropTypes.object.isRequired,
  index: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired]),
  isAbstractExpanded: PropTypes.bool.isRequired,
  toggleAbstractExpanded: PropTypes.func.isRequired,
  onIssueCitedCounterClick: PropTypes.func,
  onFullTextLinkClick: PropTypes.func,
  onAbstractOpen: PropTypes.func,
};
